import { ContentType } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { DeleteIcon } from 'components/icon/DeleteIcon';
import { ActionMenuItem } from 'components/menu/ActionMenuItem';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { sdClient } from 'contexts/SDKClientContext';
import { getDeleteFileModalText } from 'pages/Settings/FileLibrary/shared';
import { tokens } from 'styles/tokens';

import type { FileActionsBase } from './base';

function getDeleteToastMessage(fileType: ContentType, success: boolean): string {
    return success
        ? `Successfully deleted ${fileType.toLowerCase()}`
        : `Failed to delete ${fileType.toLowerCase()}`;
}

function useDeleteMutation(base: FileActionsBase) {
    return useMutation(
        base.actionMutationOptions({
            mutationFn: () => sdClient.deleteFile({ fileId: base.rowData.Id }),
            successMessage: getDeleteToastMessage(base.fileType, true),
            errorMessage: getDeleteToastMessage(base.fileType, false),
            onSuccess: () => {
                base.onChange();
                base.closeAction();
            },
        }),
    );
}

function DeleteMenuItem({ base }: { base: FileActionsBase }) {
    if (!base.canDeleteFromFileLibrary || base.enableModal) {
        return null;
    }
    return (
        <ActionMenuItem
            title="Delete"
            hoverIcon={<DeleteIcon />}
            icon={<DeleteIcon color={tokens.text.secondary} />}
            onSelect={() => base.openAction('delete')}
        />
    );
}

function DeleteModal({
    base,
    onConfirm,
}: {
    base: FileActionsBase;
    onConfirm: () => void;
}) {
    const { rowData } = base;
    const deleteText = getDeleteFileModalText({
        ...rowData,
        FileType: base.fileType,
    });
    return (
        <ConfirmModal
            title={`Delete '${rowData.Name}'`}
            actionText={deleteText.actionText}
            checkText={deleteText.checkText}
            actionButtonText="Delete"
            onAction={onConfirm}
            onClose={base.closeAction}
        />
    );
}

export const Delete = {
    useMutation: useDeleteMutation,
    MenuItem: DeleteMenuItem,
    Modal: DeleteModal,
};
